import DOMPurify from 'dompurify';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Feedback } from '@bonusly/common-feedback';
import { RelativeTime } from '@bonusly/common-relative-time';
import { Box, ChatAltSolid, Flex, rem, Spacer, StyleProps, Text } from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';

import { ReferenceUnlinkButton } from '../references-carousel/reference-unlink-button';

export interface FeedbackCardProps extends StyleProps {
	feedback: Feedback;
	onUnlinkFeedback?: (feedbackId: string) => void;
	sectionTitle?: string;
}

export const FeedbackCard: FC<FeedbackCardProps> = ({ feedback, sectionTitle, onUnlinkFeedback, ...rest }) => {
	const { t } = useTranslation(['one_on_ones', 'common']);
	const { data: giver } = useUser(feedback?.lastResponse()?.userId);
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	if (!feedback || !feedback.completedAt || feedback.messages.length === 0 || !giver) {
		return null;
	}
	searchParams.set('evaluation_id', feedback.id);
	const lastResponse = feedback.lastResponse();

	if (!lastResponse || lastResponse.text === '') {
		return null;
	}

	const text = DOMPurify.sanitize(lastResponse.text, {
		ALLOWED_TAGS: [],
	});

	const hasRequest = Boolean(feedback?.request?.text);
	const threadLength = feedback.messages.length + (hasRequest ? 1 : 0);

	const threadText = threadLength > 1 ? t('meeting.feedback_footer.thread_count', { count: threadLength }) : '';

	return (
		<Link to={`${location.pathname}?${searchParams.toString()}`}>
			<Box
				cursor="pointer"
				boxShadow="none"
				border="1px solid"
				borderColor="border.default"
				marginBottom={4}
				sx={{ breakInside: 'avoid' }}
				borderRadius="lg"
				padding={4}
				marginX={{ base: 'auto', md: 0 }}
				bg="bg.surface"
				{...rest}
			>
				<Flex gap={2} alignItems="center" borderBottom="solid" paddingBottom={3} borderColor="border.soft">
					<Flex backgroundColor="purple.50" borderRadius="full" padding="4.8px">
						<ChatAltSolid color="hashtag.fgOnSurface" />
					</Flex>

					<Text size="lg" fontWeight="semibold">
						{t('meeting.carousel.feedback')}
					</Text>

					<Spacer />

					<RelativeTime color="gray.600" date={feedback.createdAt} fontSize={{ base: 'xs', xs: 'sm' }} />

					<Flex>
						{onUnlinkFeedback && sectionTitle && (
							<ReferenceUnlinkButton onClickUnlinkReference={() => onUnlinkFeedback(feedback.id)} sectionTitle={sectionTitle} />
						)}
					</Flex>
				</Flex>

				<Box mt={3}>
					<Box maxHeight="110px" overflow="hidden">
						<Text
							display="-webkit-box"
							overflow="hidden"
							textOverflow="ellipsis"
							whiteSpace="pre-wrap"
							style={{
								WebkitBoxOrient: 'vertical',
								WebkitLineClamp: 4,
							}}
						>
							<Text as="span" fontWeight="bold" size="lg">
								{giver.displayName}:{' '}
							</Text>
							<Text as="span" size="lg">
								{text}
							</Text>
						</Text>
					</Box>

					<Flex dir="row" alignItems="center" pt={rem(8)}>
						{threadText && (
							<Text color="gray.700" size="md">
								{threadText}
							</Text>
						)}
					</Flex>
				</Box>
			</Box>
		</Link>
	);
};
